import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  TrashIcon, 
  ArrowLeftIcon, 
  CloudIcon, 
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import api from '../../services/api';

import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { stripHtml } from '../../utils/sanitize';

interface Engagement {
  id: string;
  name: string;
  number: string;
  cloudProvider: string;
  azureDetails?: any;
  awsDetails?: any;
  gcpDetails?: any;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface ResourceActivity {
  id: string;
  activityType: string;
  description: string;
  metadata?: any;
  performedAt: string;
  User: User;
}

interface Resource {
  id: string;
  name: string;
  resourceGroupName: string;
  resourceType: string;
  resourceId: string;
  cloudProvider: string;
  region: string | null;
  status: string;
  tags?: any;
  metadata?: any;
  syncedAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  createdAt: string;
  updatedAt: string;
  Engagement: Engagement;
  User_Resource_createdByIdToUser: User;
  ResourceActivity: ResourceActivity[];
}

const ResourceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const [resource, setResource] = useState<Resource | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { canViewEntity, canUpdateEntity, loading: permissionsLoading } = useUserPermissions();

  // Check if user can access resources
  const canAccessResources = canViewEntity('resources');
  const canEditResources = canUpdateEntity('resources');

  useEffect(() => {
    // Don't redirect while permissions are still loading
    if (permissionsLoading) {
      return;
    }

    if (!canAccessResources) {
      navigate('/dashboard');
      return;
    }
  }, [canAccessResources, navigate, permissionsLoading]);

  useEffect(() => {
    // Only fetch resource if we have permission and an ID
    if (permissionsLoading || !canAccessResources || !id) {
      return;
    }

    const fetchResource = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/resources/${id}`);
        setResource(response.data);
        setError(null);
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || 'Failed to fetch resource';
        setError(errorMessage);
        toast.error(errorMessage);

        if (err.response?.status === 404) {
          navigate('/resources');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResource();
  }, [id, permissionsLoading, canAccessResources, navigate]); // Add all dependencies

  const handleDelete = async () => {
    if (!resource) return;

    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete resource "${resource.name}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm'
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/resources/${resource.id}`);
              toast.success('Resource deleted successfully');
              navigate('/resources');
            } catch (err: any) {
              const errorMessage = err.response?.data?.error || 'Failed to delete resource';
              toast.error(errorMessage);
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm ml-2'
        }
      ],
      overlayClassName: 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSyncStatusBadge = (status: string | null, _error: string | null) => {
    if (!status) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Manual
        </span>
      );
    }
    
    if (status === 'success') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Synced
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
          Failed
        </span>
      );
    }
  };

  const renderMetadata = (metadata: any) => {
    if (!metadata) return null;

    return (
      <div className="space-y-2">
        {Object.entries(metadata).map(([key, value]) => (
          <div key={key} className="flex justify-between">
            <span className="text-sm font-medium text-gray-500 capitalize">
              {key.replace(/([A-Z])/g, ' $1').trim()}:
            </span>
            <span className="text-sm text-gray-900">
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderTags = (tags: any) => {
    if (!tags || typeof tags !== 'object') return null;

    return (
      <div className="flex flex-wrap gap-2">
        {Object.entries(tags).map(([key, value]) => (
          <span
            key={key}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {key}: {String(value)}
          </span>
        ))}
      </div>
    );
  };

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!canAccessResources) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-sm text-gray-700">
            You do not have permission to view resource details.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-center mt-4">Loading resource...</p>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="p-6">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
          <p className="mt-1 text-sm text-gray-500">{error || 'Resource not found'}</p>
          <div className="mt-6">
            <Link
              to="/resources"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
              Back to Resources
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div className="flex items-center">
          <Link
            to="/resources"
            className="mr-4 text-gray-400 hover:text-gray-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {resource.name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {resource.resourceType} in {resource.resourceGroupName}
            </p>
          </div>
        </div>
        {canEditResources && (
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Resource Details */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Resource Details
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Resource Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.resourceType}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Resource ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono text-xs break-all">
                    {resource.resourceId}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Resource Group</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.resourceGroupName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cloud Provider</dt>
                  <dd className="mt-1 flex items-center text-sm text-gray-900">
                    <CloudIcon className="h-4 w-4 mr-1" />
                    {resource.cloudProvider}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Region</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {resource.region || 'Not specified'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(resource.status)}`}>
                      {resource.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sync Status</dt>
                  <dd className="mt-1">
                    {getSyncStatusBadge(resource.lastSyncStatus, resource.lastSyncError)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Engagement Info */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Associated Engagement
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Engagement Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <Link
                      to={`/engagements/${resource.Engagement.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {resource.Engagement.name}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Engagement Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.Engagement.number}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Tags */}
          {resource.tags && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Tags
                </h3>
                {renderTags(resource.tags)}
              </div>
            </div>
          )}

          {/* Metadata */}
          {resource.metadata && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Cloud Properties
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  {renderMetadata(resource.metadata)}
                </div>
              </div>
            </div>
          )}

          {/* Sync Error Details */}
          {resource.lastSyncError && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Sync Error Details
                </h3>
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm text-red-800">
                        {resource.lastSyncError}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Sync Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Sync Information
              </h3>
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Synced</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {resource.syncedAt ? formatDateTime(resource.syncedAt) : 'Never'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Sync Status</dt>
                  <dd className="mt-1">
                    {getSyncStatusBadge(resource.lastSyncStatus, resource.lastSyncError)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created By</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {resource.User_Resource_createdByIdToUser.firstName}{' '}
                    {resource.User_Resource_createdByIdToUser.lastName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDateTime(resource.createdAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDateTime(resource.updatedAt)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Activity Log */}
          {resource.ResourceActivity.length > 0 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Recent Activity
                </h3>
                <div className="flow-root">
                  <ul className="-mb-8">
                    {resource.ResourceActivity.slice(0, 5).map((activity, activityIdx) => (
                      <li key={activity.id}>
                        <div className="relative pb-8">
                          {activityIdx !== resource.ResourceActivity.slice(0, 5).length - 1 ? (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                                <ArrowPathIcon className="h-4 w-4 text-white" />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500">
                                  <span className="font-medium text-gray-900">
                                    {activity.User.firstName} {activity.User.lastName}
                                  </span>{' '}
                                  <span className="lowercase">{activity.activityType}</span> this resource
                                </p>
                                <p className="mt-1 text-sm text-gray-500">{stripHtml(activity.description)}</p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                <time dateTime={activity.performedAt}>
                                  {formatDateTime(activity.performedAt)}
                                </time>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceDetail;